// src/main.ts
import './styles/base.css';
import { DEFAULT_PARAMS, type DitherParams, type Palette } from './core/types';
import { Pipeline } from './core/pipeline';
import { ALL_ALGORITHMS } from './algorithms';
import { allPalettes } from './palettes';
import { BUILTIN_PALETTES } from './palettes/builtin';
import { createViewport } from './ui/viewport';
import { createControlPanel, type Action } from './ui/control-panel';
import { randomizeParams } from './core/randomizer';
import { encodePreset, decodePreset } from './core/presets';
import { applyTheme, loadTheme } from './ui/themes';
import { attachDragDrop, openFile } from './ui/drag-drop';
import { createLoadingOverlay } from './ui/loading-overlay';
import { installMobileScroll } from './ui/mobile-scroll';

async function main() {
  applyTheme(loadTheme());
  installMobileScroll();
  const root = document.getElementById('app');
  if (!root) throw new Error('missing #app root');
  root.innerHTML = '';

  const viewport = createViewport();
  const overlay = createLoadingOverlay({ delayMs: 400 });
  viewport.el.append(overlay.el);

  const glCanvas = document.createElement('canvas');
  glCanvas.style.display = 'none';
  document.body.append(glCanvas);
  const gl = glCanvas.getContext('webgl2');
  if (!gl) {
    viewport.el.append(errorBox('WebGL2 is required. Please use a modern Chrome, Firefox, or Safari.'));
    root.append(viewport.el);
    return;
  }

  let params: DitherParams = { ...DEFAULT_PARAMS };

  const fallbackPalette: Palette = BUILTIN_PALETTES[0]!;
  const getPalette = (id: string): Palette =>
    allPalettes().find(p => p.id === id) ?? fallbackPalette;

  const pipeline = new Pipeline(gl, params, getPalette);

  let currentVideoFile: File | null = null;

  // Single-flight render loop: at most one pipeline.render() in flight at a
  // time. While one is running, additional renderSoon() calls just set the
  // dirty flag; when the current render finishes, if dirty, we schedule
  // another. This prevents render stacking that exhausts GPU / Worker queues
  // when the user scrubs sliders during slow (error-diffusion) algorithms.
  let rendering = false;
  let dirtyRender = false;
  let rafScheduled = false;

  const tick = async () => {
    rafScheduled = false;
    if (rendering || !dirtyRender) return;
    dirtyRender = false;
    rendering = true;
    overlay.show();
    try {
      const data = await pipeline.render();
      viewport.setImage(data);
    } catch (e) {
      console.error(e);
    } finally {
      overlay.hide();
      rendering = false;
      // If params changed during the render, queue another pass.
      if (dirtyRender && !rafScheduled) {
        rafScheduled = true;
        requestAnimationFrame(tick);
      }
    }
  };

  const renderSoon = () => {
    dirtyRender = true;
    if (rendering || rafScheduled) return;
    rafScheduled = true;
    requestAnimationFrame(tick);
  };

  // Cached reference to the panel's preset-code input; rebuildPanel rebinds it.
  let presetInput: HTMLInputElement | null = null;

  const onParamChange = (patch: Partial<DitherParams>) => {
    params = { ...params, ...patch };
    pipeline.setParams(params);
    if (presetInput) presetInput.value = encodePreset(params);
    renderSoon();
  };

  const onAction = async (a: Action) => {
    if (a === 'open') {
      const f = await openFile('image/*,video/*');
      if (f) await loadFile(f);
    } else if (a === 'save') {
      saveCanvasAsPng(viewport.canvas);
    } else if (a === 'export-video') {
      const { exportVideo, validateVideoFile } = await import('./video/ffmpeg-pipeline');
      if (!currentVideoFile) { alert('Load a video first'); return; }
      const valid = await validateVideoFile(currentVideoFile);
      if (!valid.ok) {
        if (!confirm(`${valid.reason}\n\nContinue anyway? (May freeze on long videos.)`)) return;
      }
      try {
        await exportVideo(pipeline, viewport, overlay, currentVideoFile, info => {
          overlay.setProgress(info.ratio, info.message);
        });
      } finally {
        overlay.clearProgress();
        overlay.hide();
      }
    } else if (a === 'reset') {
      params = { ...DEFAULT_PARAMS };
      pipeline.setParams(params);
      const next = rebuildPanel();
      controlPanel.replaceWith(next);
      controlPanel = next;
      renderSoon();
    } else if (a === 'randomize') {
      params = randomizeParams(params);
      pipeline.setParams(params);
      const next = rebuildPanel();
      controlPanel.replaceWith(next);
      controlPanel = next;
      renderSoon();
    } else if (a === 'restart') {
      window.location.reload();
    }
  };

  const rebuildPanel = (): HTMLElement => {
    const el = createControlPanel({
      params,
      algorithms: ALL_ALGORITHMS,
      palettes: allPalettes(),
      onParamChange,
      onAction,
      onPresetApply: code => {
        const patch = decodePreset(code);
        if (!patch) {
          alert('Invalid preset code');
          return;
        }
        params = { ...params, ...patch };
        pipeline.setParams(params);
        const next = rebuildPanel();
        controlPanel.replaceWith(next);
        controlPanel = next;
        renderSoon();
      },
    });
    presetInput = el.querySelector<HTMLInputElement>('[data-role="preset-code"]');
    return el;
  };

  const loadFile = async (f: File) => {
    overlay.show();
    try {
      if (f.type.startsWith('video/')) {
        const { extractFirstFrame, validateVideoFile } = await import('./video/ffmpeg-pipeline');
        const v = await validateVideoFile(f);
        if (!v.ok) {
          alert(v.reason); // Proceed anyway with first frame.
        }
        currentVideoFile = f;
        const bmp = await extractFirstFrame(f);
        await pipeline.setSource(bmp);
      } else {
        currentVideoFile = null;
        const bmp = await createImageBitmap(f);
        await pipeline.setSource(bmp);
      }
      viewport.reset();
      renderSoon();
    } finally {
      overlay.hide();
    }
  };

  attachDragDrop(viewport.el, loadFile);

  let controlPanel: HTMLElement = rebuildPanel();
  root.append(viewport.el, controlPanel);
}

function errorBox(msg: string) {
  const d = document.createElement('div');
  d.style.cssText = 'padding: 24px; color: var(--error); position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; text-align: center;';
  d.textContent = msg;
  return d;
}

function saveCanvasAsPng(canvas: HTMLCanvasElement) {
  canvas.toBlob(b => {
    if (!b) return;
    const url = URL.createObjectURL(b);
    const a = document.createElement('a');
    a.href = url; a.download = 'dither.png';
    a.click();
    URL.revokeObjectURL(url);
  }, 'image/png');
}

main().catch(err => {
  console.error(err);
  document.body.append(errorBox(`Boot failed: ${(err as Error).message}`));
});
